<template>
  <div class="telegram-editor">
    <div class="telegram-editor__toolbar">
      <button
        @click="format('bold')"
        :title="texts.bold"
      >
        <strong>B</strong>
      </button>
      <button
        @click="format('italic')"
        :title="texts.italic"
      >
        <em>I</em>
      </button>
      <button
        @click="format('underline')"
        :title="texts.underline"
      >
        <u>U</u>
      </button>

      <div
        class="telegram-emoji"
        v-click-outside="closeEmojiDropdown"
      >
        <button
          @click="toggleEmojiDropdown"
          class="telegram-emoji__btn"
        >
          {{ _("😊 Emoji") }}
        </button>
        <div
          v-if="showEmojiDropdown"
          class="telegram-emoji__menu"
        >
          <div class="telegram-emoji__group">
            <strong>{{ _("Цифри:") }}</strong>
            <button v-for="n in 10" :key="n" @click="insertEmoji(`${n-1}️⃣`)">
              {{ n-1 }}️⃣
            </button>
          </div>

          <div class="telegram-emoji__group">
            <strong>{{ _("Популярні:") }}</strong>
            <button v-for="emoji in emojiList" :key="emoji" @click="insertEmoji(emoji)">
              {{ emoji }}
            </button>
          </div>

        </div>
      </div>

      <button @click="resetToOriginal" class="reset">{{ _("Оригінал") }}</button>
    </div>

    <div
      ref="editor"
      contenteditable="true"
      class="telegram-editor__content"
      @input="onInput"
      @keydown="handleKeydown"
      @beforeinput="handleBeforeInput"
    />
  </div>
</template>

<script>
export default {
  name: 'TelegramPostEditor',

  props: {
    value: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      contentHTML: '',
      showEmojiDropdown: false,
      originalText: this.value,
      emojiList: [
        '❗️', '✅', '🔥',
        '📍', '🎯', '💰',
        '🌾', '🍒', '📄',
        '🕊️', '📊', '📈',
        '🌱', '💙', '💛',
        '🇺🇦', '🇪🇺',
      ],
      texts: {
        bold: this._('Жирний'),
        italic: this._('Курсив'),
        underline: this._('Підкреслений'),
        clear: this._('Очистити форматування'),
      },

      history: [],
      historyIndex: -1,
      maxHistory: 50,
      inputTimeout: null,
    }
  },

  mounted() {
    this.initEditor()
  },

  computed: {
    canUndo() {
      return 0 < this.historyIndex
    },

    canRedo() {
      return this.historyIndex < this.history.length - 1
    },
  },

  methods: {
    applyHistoryState() {
      const { editor } = this.$refs

      editor.innerHTML = this.history[this.historyIndex]

      this.contentHTML = editor.innerHTML

      this.$nextTick(() => {
        this.placeCursorAtEnd()
      })
    },

    undo() {
      if (!this.canUndo) return

      this.historyIndex--

      this.applyHistoryState()
    },

    redo() {
      if (!this.canRedo) return

      this.historyIndex++

      this.applyHistoryState()
    },

    placeCursorAtEnd() {
      const range = document.createRange()
      const sel = window.getSelection()

      range.selectNodeContents(this.$refs.editor)
      range.collapse(false)

      sel.removeAllRanges()
      sel.addRange(range)
    },

    handleBeforeInput(e) {
      if ('historyUndo' === e.inputType || 'historyRedo' === e.inputType) {
        e.preventDefault()

        if ('historyUndo' === e.inputType) this.undo()

        else this.redo()
      }
    },

    handleKeydown(e) {
      const isCtrl = e.ctrlKey || e.metaKey
      const key = e.key.toLowerCase()

      if (isCtrl && 'z' === key && !e.shiftKey) {
        e.preventDefault()

        this.undo()

        return
      }

      if (isCtrl && ('y' === key || ('z' === key && e.shiftKey))) {
        e.preventDefault()

        this.redo()

        return
      }

      if (isCtrl) {
        if ('b' === key) {
          e.preventDefault()

          this.format('bold')
        }

        if ('i' === key) {
          e.preventDefault()

          this.format('italic')
        }
      }

      if ('enter' === key) {
        e.preventDefault()

        const selection = window.getSelection()

        if (!selection.rangeCount) return

        const range = selection.getRangeAt(0)
        const br = document.createElement('br')

        range.deleteContents()
        range.insertNode(br)

        range.setStartAfter(br)
        range.setEndAfter(br)

        selection.removeAllRanges()
        selection.addRange(range)

        this.saveContent()
      }
    },

    saveToHistory() {
      const currentHTML = this.$refs.editor.innerHTML

      if (this.history[this.historyIndex] === currentHTML) return

      this.history = this.history.slice(0, this.historyIndex + 1)
      this.history.push(currentHTML)
      this.historyIndex = this.history.length - 1

      if (this.history.length > this.maxHistory) {
        this.history.shift()
        this.historyIndex--
      }
    },

    initEditor() {
      this.$nextTick(() => {
        const { editor } = this.$refs

        if (!editor) return

        const html = this.originalText.replace(/\n/g, '<br>')

        editor.innerHTML = html

        this.contentHTML = html

        this.saveToHistory()
      })
    },

    format(command) {
      const selection = window.getSelection()

      if (!selection || 0 === selection.rangeCount) return

      const range = selection.getRangeAt(0)

      if (range.collapsed) return

      let tag = ''

      switch (command) {
      case 'bold':
        tag = 'b'
        break
      case 'italic':
        tag = 'em'
        break
      case 'underline':
        tag = 'u'
        break
      default:
        return
      }

      this.wrapSelectionWithTag(tag, range)
      this.saveContent()
    },

    wrapSelectionWithTag(tagName, range) {
      const selection = window.getSelection()

      if (!selection) return

      if (this.isWrappedInTag(range, tagName)) {
        this.unwrapTag(range, tagName)
      } else {
        const wrapper = document.createElement(tagName)
        try {
          range.surroundContents(wrapper)
        } catch (e) {
          wrapper.appendChild(range.extractContents())
          range.insertNode(wrapper)
        }
      }

      selection.removeAllRanges()
      selection.addRange(range)
    },

    isWrappedInTag(range, tagName) {
      let node = range.commonAncestorContainer

      if (node.nodeType === Node.TEXT_NODE) {
        node = node.parentNode
      }

      while (node && node !== this.$refs.editor) {
        if (node.nodeName.toLowerCase() === tagName.toLowerCase()) {
          return true
        }
        node = node.parentNode
      }
      return false
    },

    unwrapTag(range, tagName) {
      let node = range.commonAncestorContainer

      if (node.nodeType === Node.TEXT_NODE) node = node.parentNode

      const tagToRemove = node.nodeName.toLowerCase() === tagName.toLowerCase()
        ? node
        : node.closest(tagName)

      if (!tagToRemove) return

      const parent = tagToRemove.parentNode

      while (tagToRemove.firstChild) {
        parent.insertBefore(tagToRemove.firstChild, tagToRemove)
      }

      parent.removeChild(tagToRemove)
    },

    insertEmoji(emoji) {
      this.$refs.editor.focus()

      const selection = window.getSelection()

      if (!selection) return

      const range = selection.getRangeAt(0)

      range.deleteContents()

      const textNode = document.createTextNode(emoji)

      range.insertNode(textNode)

      range.setStartAfter(textNode)
      range.setEndAfter(textNode)

      selection.removeAllRanges()
      selection.addRange(range)

      this.saveContent()

      this.closeEmojiDropdown()
    },

    closeEmojiDropdown() {
      this.showEmojiDropdown = false
    },

    toggleEmojiDropdown() {
      this.showEmojiDropdown = !this.showEmojiDropdown
    },

    closeDropdownOnOutsideClick(e) {
      if (!this.$el.contains(e.target)) {
        this.closeEmojiDropdown()
      }
    },

    prepareContentForOutput(html) {
      return html.replace(/<br>/g, '\n').replace(/\n+$/, '')
    },

    saveContent() {
      this.contentHTML = this.$refs.editor.innerHTML

      this.$emit('input', this.prepareContentForOutput(this.contentHTML))

      this.saveToHistory()
    },

    onInput() {
      this.saveContent()

      clearTimeout(this.inputTimeout)
    },

    resetToOriginal() {
      this.initEditor()
    },

    beforeDestroy() {
      clearTimeout(this.inputTimeout)
    },
  },
}
</script>
